import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, HelpCircle, Mail } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { seoHead } from "@/lib/seo";

export const Route = createFileRoute("/help")({
  head: () => {
    const base = seoHead({
      title: "ヘルプ・よくある質問 - KAGEM",
      description:
        "KAGEMの使い方、クレジットや課金、サブスクリプションの解約、生成エラーへの対処など、よくある質問と解決方法をまとめています。困ったときはまずこちらをご覧ください。",
      path: "/help",
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  component: HelpPage,
});

const FAQS: { q: string; a: string }[] = [
  {
    q: "KAGEMとは何ですか？",
    a: "KAGEMはAIを使って画像や動画を生成できるクリエイティブツールです。テンプレートやプロンプトから簡単にコンテンツを作成できます。",
  },
  {
    q: "クレジットとは何ですか？",
    a: "クレジットは生成に必要なポイントです。画像生成や動画生成のたびに、モデルや品質に応じたクレジットを消費します。クレジット購入ページから追加購入できます。",
  },
  {
    q: "クレジットの有効期限はありますか？",
    a: "購入したクレジットには有効期限があります。詳しくは特定商取引法に基づく表記および購入ページをご確認ください。",
  },
  {
    q: "サブスクリプションを解約するには？",
    a: "「設定」＞「プラン」タブから解約できます。月契約はいつでも解約可能で、次回課金日まで引き続きご利用いただけます。年契約（12ヶ月縛りの月払い）は解約リクエストをいつでも受け付けますが、12ヶ月目の課金完了まで請求が継続し、終了日に自動解約されます。",
  },
  {
    q: "生成に失敗した場合、クレジットは返ってきますか？",
    a: "システム側のエラーで生成に失敗した場合、消費したクレジットは自動的に返還されます。返還されない場合はお問い合わせください。",
  },
  {
    q: "生成した画像・動画の著作権は？",
    a: "ご自身が生成したコンテンツの利用権はユーザーに帰属します。詳細は利用規約をご確認ください。",
  },
  {
    q: "領収書は発行できますか？",
    a: "「請求履歴」ページから各決済の領収書をダウンロードできます。",
  },
  {
    q: "不適切なコンテンツを見つけたら？",
    a: "通報ページから内容を報告いただけます。確認の上、適切に対応いたします。",
  },
  {
    q: "アカウントを削除したい",
    a: "「設定」ページからアカウント削除のリクエストができます。削除後はデータ復旧できませんのでご注意ください。",
  },
  {
    q: "それ以外のトラブル・要望は？",
    a: "ページ上部の「お問い合わせ」ボタンからフォームでご連絡ください。通常2〜3営業日以内に返信いたします。",
  },
];

function HelpPage() {
  return (
    <main className="min-h-screen bg-background px-4 py-12">
      <div className="mx-auto max-w-3xl">
        <Button variant="ghost" size="sm" asChild className="mb-4">
          <Link to="/">
            <ArrowLeft className="mr-1 h-4 w-4" />
            トップへ戻る
          </Link>
        </Button>

        <Card className="p-6 sm:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10">
                <HelpCircle className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight">ヘルプ</h1>
                <p className="text-sm text-muted-foreground">よくある質問</p>
              </div>
            </div>

            <Button asChild>
              <Link to="/contact">
                <Mail className="mr-2 h-4 w-4" />
                お問い合わせ
              </Link>
            </Button>
          </div>

          <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
            ご不明点は下記のよくある質問をご確認ください。解決しない場合は上部の
            「お問い合わせ」ボタンからご連絡ください。
          </p>

          <Accordion type="single" collapsible className="mt-6">
            {FAQS.map((item, i) => (
              <AccordionItem key={i} value={`item-${i}`}>
                <AccordionTrigger className="text-left">{item.q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-8 rounded-lg border border-border/60 bg-muted/30 p-5 text-center">
            <p className="text-sm text-muted-foreground">解決しませんでしたか？</p>
            <Button asChild className="mt-3">
              <Link to="/contact">
                <Mail className="mr-2 h-4 w-4" />
                お問い合わせフォームへ
              </Link>
            </Button>
          </div>
        </Card>
      </div>
    </main>
  );
}
